import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { ArrowRightIcon } from '@blade/ui-kit'
import { premiumConfigAtom } from '@root/entities/premium'
import { useCurrencyExpirationLeft } from '@root/entities/premium/hooks'
import { PremiumCurrencyInfo } from '@root/entities/premium/ui'
import { reatomPurchaseBoosters } from '@root/features/premium/purchase-boosters'
import { DATE_FORMAT } from '@root/shared'

import { PaymentDialogSuccess } from '../desktop/common/payment-dialog-success'
import { PaymentDrawerSuccess } from '../mobile/common/payment-drawer-success'

import { ExtendPremiumForm } from './extend-premium-form'

import type { DeviceType } from '@blade/ui-kit'
import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium/atoms'

type Props = {
  currency: CurrencyModel
  premium: PremiumMetaModel
  onClose: () => void
  device: DeviceType
}

export const ExtendPremium: React.FC<Props> = ({
  currency,
  premium,
  onClose,
  device,
}) => {
  const { t } = useTranslation()
  const [{ premiumExpirationDays }] = useAtom(premiumConfigAtom)
  const [expiresAt] = useAtom(premium.expiresAt)
  const [premiumId] = useAtom(premium.premiumId)
  const premiumIdRef = useLatest(premiumId)

  const model = useMemo(
    () =>
      reatomPurchaseBoosters(
        { currency, type: 'extend', premiumId: premiumIdRef.current },
        'model',
      ),
    [currency, premiumIdRef],
  )

  const [requestState] = useAtom(model.requestStateAtom)
  const { formatExpirationLeft } = useCurrencyExpirationLeft()

  const [expireAt, expireTo] = useMemo(() => {
    return [expiresAt, DateF.addDays(expiresAt, premiumExpirationDays)]
  }, [expiresAt, premiumExpirationDays])

  const PaymentSuccess =
    device === 'mobile' ? PaymentDrawerSuccess : PaymentDialogSuccess

  return (
    <Body>
      <SubTitle>
        <span>
          {t('premium.purchased.until', {
            date: DateF.format(expireAt, DATE_FORMAT.dd_MM_yy),
          })}
        </span>
        {formatExpirationLeft(currency, expireAt)}
        <ArrowRightIcon />
        <span>
          {t('premium.purchased.until', {
            date: DateF.format(expireTo, DATE_FORMAT.dd_MM_yy),
          })}
        </span>
        {formatExpirationLeft(currency, expireTo)}
      </SubTitle>
      <Info premium={premium} currency={currency} />
      {AsyncStateN.isSuccess(requestState) ? (
        <PaymentSuccess
          type="premium"
          transactionHash={requestState.value.transactionHash}
          onClose={() => {
            onClose()
          }}
        />
      ) : (
        <ExtendPremiumForm model={model} currency={currency} device={device} />
      )}
    </Body>
  )
}

const Info = styled(PremiumCurrencyInfo)`
  margin-bottom: 16px;
`

const Body = styled.div`
  overflow: hidden;
  display: grid;

  padding-inline: 30px;
  padding-block: 8px 20px;
`

const SubTitle = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  color: var(--text-9);

  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;

  margin-top: 6px;
  margin-bottom: 32px;

  span {
    color: var(--text-12);
  }
`
